/**
 * Manual accidental overrides.
 *
 * Recognition is honest but not infallible, and sometimes you simply want to say
 * "that one is a flat" without redrawing it. An override edits the *semantic* note,
 * so the engraved score, the chord name, and playback all follow.
 *
 * Precedence, highest first:
 *   1. a manual override set here
 *   2. an accidental actually drawn next to the notehead
 *   3. the key signature
 *
 * Overrides are keyed on the strokes that produced the note, so they survive
 * re-recognition when you write the next note, and disappear when you erase the ink
 * they belong to.
 */

import { positionToPitch, type Clef } from '../theory/staff';
import type { RecognitionResult, RecognizedNote } from './recognize';

/** A forced alteration, or null for "Auto" — defer to drawn ink and key signature. */
export type AccidentalOverride = number | null;

export type OverrideMap = Record<string, AccidentalOverride>;

/** Stable identity for a recognised note: the strokes it was read from. */
export function noteKey(note: RecognizedNote): string {
  return note.strokeIds.join('~');
}

/**
 * Re-derive pitches with manual overrides applied. Returns a new result; the
 * recogniser's own output is left untouched so the two can always be compared.
 */
export function applyOverrides(
  result: RecognitionResult,
  overrides: OverrideMap,
  clef: Clef,
): RecognitionResult {
  if (!Object.keys(overrides).length) return result;

  const replaced = new Map<RecognizedNote, RecognizedNote>();

  const override = (note: RecognizedNote): RecognizedNote => {
    const existing = replaced.get(note);
    if (existing) return existing;

    const forced = overrides[noteKey(note)];
    if (forced === undefined || forced === null) {
      replaced.set(note, note);
      return note;
    }

    const next: RecognizedNote = {
      ...note,
      pitch: positionToPitch(clef, note.position, forced),
      writtenAccidental: forced,
      // A note you corrected by hand is certain by definition.
      confidence: Math.max(note.confidence, 0.99),
    };
    replaced.set(note, next);
    return next;
  };

  const notes = result.notes.map(override);
  const groups = result.groups.map((group) => group.map(override));

  return { ...result, notes, groups };
}

/** Forget overrides whose ink no longer exists, so erasing really does erase. */
export function pruneOverrides(overrides: OverrideMap, result: RecognitionResult): OverrideMap {
  const live = new Set(result.notes.map(noteKey));
  const next: OverrideMap = {};
  for (const [key, value] of Object.entries(overrides)) {
    if (live.has(key)) next[key] = value;
  }
  return next;
}

export const ACCIDENTAL_CHOICES: { value: AccidentalOverride; label: string; title: string }[] = [
  { value: null, label: 'Auto', title: 'Use the drawn accidental, or the key signature' },
  { value: 0, label: '♮', title: 'Force a natural' },
  { value: 1, label: '♯', title: 'Force a sharp' },
  { value: -1, label: '♭', title: 'Force a flat' },
];
